import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'

import { EligibleEmployeeOptedInStatusStatistic, PlanPeriodDto, getEligibleEmployeeOptedInStatusStatistic } from 'core'

import Pie from 'common/components/charts/Pie'

import { Button, Card, Icon, Skeleton, Tooltip } from 'common/components'
import { useQueryRequestV2, useSharedState } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import styles from '../index.module.less'

type InitialPremiumStatusesProps = {
  planPeriod: PlanPeriodDto
}

const InitialPremiumStatuses = ({ planPeriod }: InitialPremiumStatusesProps) => {
  const history = useHistory()
  const [, setFilteredInfo] = useSharedState(`eligible-employee-table-table-filter`, {})

  const {
    data: optedIntatusStatistic,
    isLoading: isOptedIntatusStatistic,
    sendQuery: sendGetOptedIntatusStatistic,
  } = useQueryRequestV2<EligibleEmployeeOptedInStatusStatistic>({
    initialState: null,
    queryFn: () => getEligibleEmployeeOptedInStatusStatistic(planPeriod.id),
  })

  const percentageOptedIn = (
    optedIntatusStatistic?.activeOptedInEligibleEmployees === 0
      ? 0
      : (optedIntatusStatistic?.premiumReimbursed * 100) / optedIntatusStatistic?.activeOptedInEligibleEmployees
  ).toFixed(0)

  useEffect(() => {
    sendGetOptedIntatusStatistic(() => getEligibleEmployeeOptedInStatusStatistic(planPeriod.id))
  }, [planPeriod.id])

  return (
    <Skeleton loading={isOptedIntatusStatistic}>
      {optedIntatusStatistic && (
        <Card className="h-100 w-100 t-onboarding-statuses">
          <div className="">
            <div className="mb-16 flex jc-space-between align-center">
              <h5 className="mb-0">Opted-in employee initial premium statuses</h5>
            </div>
            <div className={styles.statCard}>
              <Button
                className="flex align-center h-100 p-0"
                type="text"
                onClick={() => {
                  history.push({
                    pathname: ROUTE_PATHS.MANAGE_PEOPLE,
                    state: { orderColumn: 'benefitParticipationStatus' },
                  })
                  setFilteredInfo({ benefitParticipationStatus: [1] })
                }}
              >
                {optedIntatusStatistic && (
                  <Pie
                    data={[
                      {
                        type: 'Onboarding Statuses',
                        value: Number(percentageOptedIn),
                        color: '#11D175',
                      },
                    ]}
                    statistics={{
                      title: `${percentageOptedIn}%`,
                      content: `(${optedIntatusStatistic?.premiumReimbursed}/${optedIntatusStatistic?.activeOptedInEligibleEmployees})`,
                    }}
                    width={115}
                    height={115}
                    style={{
                      statisticTitle: {
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#27292C',
                      },
                      statisticContent: {
                        fontSize: '14px',
                        marginTop: '4px',
                        color: '#858C96',
                      },
                    }}
                  />
                )}
                <div className="flex flex__direction_column flex__space_between_baseline ml-8">
                  <div className="text-semi-bold color-gray-9 font-24 mb-8">
                    {optedIntatusStatistic.activeOptedInEligibleEmployees} opted in
                  </div>
                  <div className="color-gray-7">of {optedIntatusStatistic.activeEligibleEmployees} eligible</div>
                </div>
              </Button>
              <Card className={styles.smallCard}>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4"> Annually attested </div>
                    <Tooltip title="These employees have completed their legally required ICHRA annual attestation.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">
                    {optedIntatusStatistic.annuallyAttested}/{optedIntatusStatistic.activeOptedInEligibleEmployees}
                  </div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Submitted a premium</div>
                    <Tooltip title="These employees have successfully submitted a qualified individual insurance premium reimbursement expense.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">
                    {optedIntatusStatistic.premiumSubmitted}/{optedIntatusStatistic.annuallyAttested}
                  </div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Verified a premium</div>
                    <Tooltip title="These employees have had their qualified individual insurance premium reimbursement request successfully verified by PeopleKeep.">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">
                    {optedIntatusStatistic.premiumVerified}/{optedIntatusStatistic.premiumSubmitted}
                  </div>
                </div>
                <div className={styles.statCardElement}>
                  <div className="flex flex__center color-gray-7 mb-8">
                    <div className="mr-4">Reimbursed a premium</div>
                    <Tooltip title="You’ve successfully scheduled a premium reimbursement for these employees!">
                      <Icon className="far fa-info-circle color-cyan-6" />
                    </Tooltip>
                  </div>
                  <div className="color-gray-9 font-24 text-semi-bold">
                    {optedIntatusStatistic.premiumReimbursed}/{optedIntatusStatistic.premiumVerified}
                  </div>
                </div>
              </Card>
            </div>

            {/* <div className="flex">
              <div className="mr-16">
                <Badge status="success" className="mr-4" /> Opted in
              </div>
              <div>
                <Badge status="default" className="mr-4" /> Total employees
              </div>
            </div> */}
          </div>
        </Card>
      )}
    </Skeleton>
  )
}

export default InitialPremiumStatuses
